import { H5, InputText } from "@sellar-io/components";
import { Icon, ListElement } from "../../../../components";
import {
  NextPayoutResponseType,
  PayoutListResponseType,
} from "@sellar-io/api-v2/types";
import { twMerge } from "tailwind-merge";
import { formatCurrency } from "@sellar-io/common";
import { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { InputContainer } from "@sellar-io/components";
import { SELLAR_BUSINESS_ID } from "../../../../constants/sellarConstants.shared";

import { SellarCreditDrawer } from "./SellarCreditDrawer";
import { SellarCreditDocument } from "./PayoutTable.shared";
import { type DocumentType } from "../stores/PayoutFilterStore";
import { Dot } from "./SellarCreditStats";

const displayType = (type: DocumentType) => {
  switch (type) {
    case "invoice":
      return "Inv";
    case "credit-note":
      return "CrN";
    case "fee":
      return "Fee";
  }
};

interface SellarCreditDocumentWithCustomer extends SellarCreditDocument {
  customer: string;
}

const PayoutCard = ({
  item,
  setActive,
  setDocument,
}: {
  item: SellarCreditDocumentWithCustomer;
  setActive: (value: boolean) => void;
  setDocument: (value: SellarCreditDocumentWithCustomer) => void;
}) => {
  return (
    <ListElement
      onClick={() => {
        setActive(true);
        setDocument(item);
      }}
    >
      <div className={"flex flex-col gap-1"}>
        <span className="text-xs">
          {displayType(item.type)} #{item.contents.id}{" "}
          {"OrderId" in item.contents &&
            item.type === "invoice" &&
            `• Order #${item.contents.OrderId || ""}`}
        </span>
        <span className="text-base">
          {dayjs(item.contents.createdAt).format("DD/MM/YYYY")}
        </span>
        {item.type === "fee" ? (
          <span className="text-sm">
            Total: {formatCurrency(item.contents.total)}
          </span>
        ) : (
          <span className="text-sm">
            {item.customer} &bull; Total: {formatCurrency(item.contents.total)}
          </span>
        )}
      </div>
    </ListElement>
  );
};

export const PayoutCardList = ({
  payout,
  setActive,
}: {
  payout: PayoutListResponseType[number] | NextPayoutResponseType;
  setActive: (value: boolean) => void;
}) => {
  const [search, setSearch] = useState("");
  const [selectedDocument, setSelectedDocument] =
    useState<SellarCreditDocument>();
  const [activeDocument, setActiveDocument] = useState(false);

  const data = useMemo<SellarCreditDocumentWithCustomer[]>(() => {
    if (!payout) return [];

    const feeInvoiceLocal =
      payout.Invoices.find(
        (invoice) =>
          invoice.type === "sellarCreditSupplier" &&
          invoice.SenderBusinessId === SELLAR_BUSINESS_ID,
      ) || null;

    const invoiceRows = payout.Invoices.filter(
      (invoice) => !feeInvoiceLocal || invoice.id !== feeInvoiceLocal.id,
    ).map((invoice) => ({
      type: "invoice" as DocumentType,
      contents: invoice,
      customer:
        invoice.InvoiceMetaData?.recipientDeliveryAddressName ||
        invoice.RecipientBusiness.name,
    }));

    const feeRow = feeInvoiceLocal
      ? {
          type: "fee" as DocumentType,
          contents: feeInvoiceLocal,
          customer: feeInvoiceLocal.RecipientBusiness.name,
        }
      : null;

    const creditNoteRows = (payout.CreditNotes || []).map((creditNote) => ({
      type: "credit-note" as DocumentType,
      contents: creditNote,
      customer: creditNote.RecipientBusiness.name,
    }));

    return [...invoiceRows, ...creditNoteRows, ...(feeRow ? [feeRow] : [])];
  }, [payout]);

  // Filter rows by search and by document type from the store.
  const filteredData = useMemo(() => {
    let tempData = data;
    if (search) {
      tempData = tempData.filter(
        (row) =>
          row.contents.RecipientBusiness.name
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          row.contents.id
            ?.toString()
            .toLowerCase()
            .includes(search.toLowerCase()),
      );
    }
    return tempData;
  }, [data, search]);

  const { feeInvoice, invoicesTotal, creditNotesTotal } = useMemo(() => {
    const feeInvoice = payout.Invoices.find(
      (invoice) =>
        invoice.type === "sellarCreditSupplier" &&
        invoice.SenderBusinessId === SELLAR_BUSINESS_ID,
    ) || { id: "", total: 0, RecipientBusiness: { name: "" } };

    const invoicesTotal =
      payout.Invoices.reduce((acc, invoice) => {
        if (invoice.SenderBusinessId !== SELLAR_BUSINESS_ID) {
          return acc + (invoice.total || 0);
        }
        return acc;
      }, 0) || 0;

    const creditNotesTotal =
      payout.CreditNotes.reduce(
        (acc, creditNote) => acc + (creditNote.total || 0),
        0,
      ) || 0;

    return { feeInvoice, invoicesTotal, creditNotesTotal };
  }, [payout]);

  function handleRowClick(rowData: SellarCreditDocument) {
    if (!payout) return;
    if (rowData.type === "credit-note") {
      const creditNote = payout.CreditNotes.find(
        (cn) => cn.id === rowData.contents.id,
      );
      if (creditNote) {
        setSelectedDocument({ type: "credit-note", contents: creditNote });
      }
    } else {
      const invoice = payout.Invoices.find(
        (inv) => inv.id === rowData.contents.id,
      );
      if (invoice) {
        setSelectedDocument({ type: rowData.type, contents: invoice });
      }
    }
    setActiveDocument(true);
  }

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  return (
    <div
      className={twMerge(
        "[scrollbar-width:none] overflow-auto border-zinc-300 [::-webkit-scrollbar]:hidden",
      )}
      ref={scrollContainerRef}
    >
      <div
        className={twMerge(
          "flex w-full border-collapse flex-wrap items-center justify-between py-4",
        )}
      >
        <div className="flex w-full flex-col items-center">
          <H5 className="mb-3 flex w-full flex-wrap items-center space-x-1 bg-white px-4 text-sm font-medium">
            <div
              className="cursor-pointer hover:underline"
              onClick={() => setActive(false)}
            >
              Sellar Credit
            </div>
            <Icon icon="CHEVRON_RIGHT" className="m-0" />
            {payout.id ? `Payout # ${payout.id}` : "Current Balance"}
          </H5>
          <div className="ms-4 inline-flex self-start rounded-lg border border-zinc-300 bg-white py-4">
            <div className="flex flex-col gap-2 border-r px-6">
              <span className="text-xs font-semibold">Total order amount</span>
              <span className="text-xl">
                {formatCurrency(invoicesTotal - creditNotesTotal)}
              </span>
            </div>
            <div className="flex flex-col gap-2 px-6">
              <span className="text-xs font-semibold">Net payout</span>
              <span className="flex items-center gap-1 text-xl text-zinc-800">
                <Dot color="bg-emerald-800" />
                {formatCurrency(
                  invoicesTotal - (creditNotesTotal + feeInvoice.total),
                )}
              </span>
            </div>
          </div>
          <div className="mt-3 flex w-full border-y border-zinc-300 bg-zinc-50 p-4">
            <InputContainer className="mr-2 w-full min-w-[300px] py-[0.4rem]">
              <InputText
                required={false}
                placeholder="Search order #, customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </InputContainer>
          </div>
        </div>
      </div>
      {filteredData.map((item, index) => (
        <PayoutCard
          item={item}
          key={`${index}-${item.contents.id}`}
          setActive={setActive}
          setDocument={handleRowClick}
        />
      ))}
      <SellarCreditDrawer
        active={activeDocument}
        setActive={() => setActiveDocument(false)}
        document={selectedDocument}
      />
    </div>
  );
};
